<script setup lang="ts">
import {ref} from "vue";
import {FilterField, WidgetProps} from "@/utils/presenters/interfaces.ts";
import WidgetContainer from "@/components/EditView/widgets/WidgetContainer.vue";
import ErrorTooltip from "@/components/EditView/ErrorTooltip.vue";
import DropDownFieldWidgetUtil from "@/utils/editView/widgets/DropDownFieldWidgetUtil.ts";
import {QSelect} from "quasar";
import {focusAndSelectInput, NamaNavigateToNextEmit} from "@/utils/common/NamaVueWidget.ts";
import NamaIcon from "@/components/icons/NamaIcon.vue";
import {useNamaFlagsStore} from "@/stores/nama-flags-store.ts";
import Translator from "@/utils/common/Translator.ts";
import SoundEffects from "@/utils/constants/SoundEffects.ts";
import NotificationUtils from "@/utils/broadcast/NotificationUtils.ts";

const props = defineProps<WidgetProps>();
const fieldId = props.field.id!;
const popupVisible = ref<Boolean>(false);
const fieldUtil = new DropDownFieldWidgetUtil(props.presenter, props.fieldMetaData, props.doNotApplyThemeLayouts ? (props.field as FilterField).allowedValues : undefined);
const displayedAllowedValues = fieldUtil.allowedValues;
const options = ref(displayedAllowedValues);
const emit = defineEmits<NamaNavigateToNextEmit>()

function dropDrownFilter(val: any, update: any) {
  DropDownFieldWidgetUtil.dropDownFilterFn(options, fieldUtil, val, update);
}

function onEnterKeyDown(e: KeyboardEvent, doNotGoToNextWithEnter?: boolean) {
  if (popupVisible.value || doNotGoToNextWithEnter)
    return;
  e.preventDefault();
  e.stopPropagation();
  setTimeout(() => emit("navigateToNext", {keyboardEvent: e}), 100);
}

const input = ref<QSelect>()

function focus() {
  focusAndSelectInput(input);
}

function canReceiveFocus() {
  return !fieldUtil.isFieldDisabled(props.forceEnableField);
}

function limitedAllowedValues() {
  if (!useNamaFlagsStore().showFieldInfoActive)
    return ""
  const readOnlyOptions = () => options.value
  return readOnlyOptions().map((el) => el.id).join(",")
}

function playSound() {
  if (!hasSound())
    return;
  const audio = NotificationUtils.createAudio(fieldUtil.formattedValue().value?.id ?? SoundEffects.None);
  if (audio)
    audio.play().catch(err => console.error('Failed to play warning sound:', err)).finally(() => console.log('task audio played'));
}

function hasSound() {
  return fieldUtil.formattedValue().value && fieldUtil.formattedValue().value?.id != SoundEffects.None;
}

defineExpose({focus, canReceiveFocus})
</script>

<template>
  <WidgetContainer
    :field="field"
    :entityType="presenter.entityType" :isSpanned="isSpanned" :isFieldDisabled="fieldUtil.isFieldDisabled(forceEnableField)"
    :isFieldRequired="fieldUtil.isFieldRequired()" :doNotApplyThemeLayouts="doNotApplyThemeLayouts"
    :isInsideComposite="isInsideComposite">
    <template #icon>
      <NamaIcon :presenter="presenter" :block="field" class="tw-size-[22px] tw-text-[22px]"/>
    </template>
    <template v-slot:widget="{ showAsPassword, doNotGoToNextWithEnter }">
      <div class="tw-flex tw-flex-row">
        <q-select :readonly="fieldUtil.isFieldDisabled(forceEnableField)"
                  @popup-show="popupVisible = true" @popup-hide="popupVisible = false"
                  @keydown.enter.capture="(e) => onEnterKeyDown(e, doNotGoToNextWithEnter)"
                  :type="showAsPassword ? 'password' : 'text'"
                  hide-bottom-space dense options-dense outlined use-input hide-selected fill-input
                  :id="field.id" ref="input"
                  :data-enum-type-name="fieldMetaData.enumTypeName"
                  :data-limited-allowed-values="limitedAllowedValues()"
                  :model-value="fieldUtil.formattedValue().value"
                  @update:model-value="fieldUtil.onSelect"
                  @input-value="fieldUtil.onPartialUserInput"
                  @focus="fieldUtil.onFocus" @blur="fieldUtil.onBlur"
                  :options="options"
                  option-label="title"
                  option-value="id"
                  @filter="dropDrownFilter"
                  input-debounce="0"
                  :error="!!props.presenter.fetchFieldErrorMsg(fieldId)"
                  class="tw-w-full nama-customize-dense-input"
                  :class="{'nama-field-input-disabled':fieldUtil.isFieldDisabled(forceEnableField),'nama-field-input':!fieldUtil.isFieldDisabled(forceEnableField)}"
                  input-class="tw-text-base tw-font-normal dark:tw-text-white tw-bg-transparent focus:tw-ring-0"
                  options-selected-class="tw-text-base active-white tw-font-normal tw-border-b-black tw-bg-primary-light dark:tw-bg-primary-dark tw-text-white nama-icon-active"
                  popup-content-class="!tw-max-h-[30vh] tw-text-base tw-font-normal dark:tw-text-white dark:tw-bg-namaBackground-bright-dark">
          <template v-slot:option="scope">
            <q-item v-bind="scope.itemProps">
              <q-item-section>
                <q-item-label class="tw-text-sm dark:tw-text-white" v-text="scope.opt.title"/>
              </q-item-section>
              <q-item-section side>
                <NamaIcon :enumTypeName="fieldMetaData.enumTypeName" :allowed-value="scope.opt.id" :key="scope.opt.id"/>
              </q-item-section>
            </q-item>
          </template>
          <ErrorTooltip v-if="presenter.fetchFieldErrorMsg(fieldId)" :error-msg="presenter.fetchFieldErrorMsg(fieldId)"/>
        </q-select>
        <div class="tw-pl-1">
          <q-avatar square @click.stop="playSound" :title="Translator.translateId('playSound')"
                    class=" tw-size-7 compact:tw-size-[23px] ultra:tw-size-[20px] "
                    :class="[hasSound()?'hover:tw-bg-[#ECF0F7] hover:tw-rounded dark:hover:tw-bg-namaGray tw-cursor-pointer':'']">
            <NamaIcon defaultIcon="volume_up" class="tw-size-[18px] !tw-text-[18px]"
                      :class="[hasSound()?'':'active-gray']"/>
          </q-avatar>
        </div>
      </div>
    </template>
  </WidgetContainer>
</template>

<style>

</style>
